import { useCallback, useMemo, useRef, useState } from 'react';
import { useHasChangedDeep, useHasChangedShallow } from './use-utils.hooks';
import {
  filterObject,
  getKeys,
  mapObject,
} from '@shared/helpers/object.helpers';
import { toLookup } from '@shared/helpers/utils.helpers';
import { ValidIdItem } from '@shared/models/type-helpers.model';

/**
 * Similar to useMemo but will memoize an object until one of the objects properties change using shallow comparison.
 * Works with arrays and objects.
 * @returns
 */
export function useObjectMemo<T extends object>(obj: T): T {
  const hasChanged = useHasChangedShallow(obj);
  const objRef = useRef<T | undefined>(undefined);

  if (hasChanged) {
    objRef.current = obj;
  }
  return objRef.current as T;
}

/**
 * Similar to useMemo but will memoize an object until one of the objects properties change using deep comparison.
 * Works with arrays and objects.
 * @returns
 */
export function useObjectMemoDeep<T extends object>(obj: T): T {
  const hasChanged = useHasChangedDeep(obj);
  const objRef = useRef<T | undefined>(undefined);

  if (hasChanged) {
    objRef.current = obj;
  }
  return objRef.current as T;
}

/**
 * Will create a filter object that will update when the filter changes.
 * Will not trigger updates when filter function changes so no need to memoize it.
 * @returns
 */
export const useFilterObject = <T extends object, K extends keyof T>(
  obj: T,
  filter: (value: T[K], key: K) => boolean | undefined,
): Partial<T> => {
  const filterRef = useRef(filter);
  filterRef.current = filter;
  return useMemo(() => filterObject(obj, filterRef.current), [obj]);
};

/**
 * Will map an object using a function and return a new object with the mapped values.
 * Will trigger updates when obj or map function changes.
 * @param obj
 * @param map
 * @returns
 */
export const useMapObject = <T extends object, K extends keyof T, R>(
  obj: T,
  map: (value: T[K], key: K) => R,
): Record<K, R> => {
  const mapped = useMemo(() => mapObject(obj, map), [obj, map]);
  return useObjectMemo(mapped);
};

/**
 * Will create an id-lookup (Record<id, T>) record from an array of items.
 * Will trigger updates when items change.
 * @param items
 * @returns
 */
export const useLookup = <T extends ValidIdItem>(
  items: T[],
): Record<T['id'], T> => {
  return useMemo(() => toLookup(items), [items]);
};

/**
 * A hook that manages object state with individual property updates.
 * Only triggers updates when the value actually changes.
 *
 * @param initialState - The initial state object
 * @returns [state, setProperty] - The current state and a function to update individual properties
 *
 * @example
 * const [state, setProperty] = useObjectState({ count: 0, name: '' });
 * setProperty('count', 1); // Updates only if count !== 1
 * setProperty('name', 'John'); // Updates only if name !== 'John'
 */
export function useSetObjectValueState<T extends Record<string, any>>(
  initialState: T,
): [T, <K extends keyof T>(key: K, value: T[K]) => void] {
  const [state, setState] = useState<T>(initialState);

  const setProperty = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setState((prevState) => {
      // Only update if the value has actually changed
      if (prevState[key] === value) {
        return prevState;
      }
      return {
        ...prevState,
        [key]: value,
      };
    });
  }, []);

  return [state, setProperty];
}

/**
 * Will return the keys of an object (typesafe and memoized)
 * @param obj
 * @param sort - Sort the keys alphabetically (can be useful to guarantee execution order)
 * @returns
 */
export const useKeys = <T extends object>(
  obj: T,
  sort?: Parameters<typeof getKeys>[1],
): (keyof T)[] => {
  return useMemo(() => getKeys(obj, sort), [obj, sort]);
};
